import { getHcoNameSynonyms } from './hcoNameSynonyms';
import {
	escapeHtml,
	normalizeSearchText as normalize
} from './searchTextHelpers';

const LEADING_PUNCTUATION = /^[^\p{L}\p{N}]*/u;
const SUB_WORD = /[^-]+/g;
const MERGEABLE_GAP = /^[\s-]*$/;

const FUZZY_MIN_TERM_LENGTH = 4;
const FUZZY_HIGH_EDIT_TERM_LENGTH = 8;

type WordEntry = {
	sequence: number;
	start: number;
	core: string;
	normalizedCore: string;
};

type MatchRange = {
	start: number;
	length: number;
};

const isPrefixMatch = (word: string, term: string) =>
	normalize(word).startsWith(normalize(term));

const hasSynonymPrefixMatch = (word: string, term: string) =>
	getHcoNameSynonyms(word).some(synonym => isPrefixMatch(synonym, term));

const editDistance = (a: string, b: string): number => {
	const distances = Array.from({ length: a.length + 1 }, (_, i) =>
		Array.from({ length: b.length + 1 }, (_, j) =>
			i === 0 ? j : j === 0 ? i : 0
		)
	);

	for (let i = 1; i <= a.length; i++) {
		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			distances[i][j] = Math.min(
				distances[i - 1][j] + 1,
				distances[i][j - 1] + 1,
				distances[i - 1][j - 1] + cost
			);

			if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
				distances[i][j] = Math.min(
					distances[i][j],
					distances[i - 2][j - 2] + 1
				);
			}
		}
	}

	return distances[a.length][b.length];
};

const maxFuzzyEditsForTerm = (term: string) =>
	term.length >= FUZZY_HIGH_EDIT_TERM_LENGTH ? 2 : 1;

const findFuzzyMatch = (
	words: WordEntry[],
	term: string
): WordEntry | undefined => {
	const normalizedTerm = normalize(term);
	const maxEdits = maxFuzzyEditsForTerm(normalizedTerm);

	return words.find(
		word => editDistance(word.normalizedCore, normalizedTerm) <= maxEdits
	);
};

const mergeAdjacentRanges = (
	ranges: MatchRange[],
	name: string
): MatchRange[] => {
	const merged: MatchRange[] = [];

	ranges.forEach(range => {
		const last = merged[merged.length - 1];
		const lastEnd = last ? last.start + last.length : -1;

		if (last && MERGEABLE_GAP.test(name.slice(lastEnd, range.start))) {
			last.length = Math.max(lastEnd, range.start + range.length) - last.start;
			return;
		}

		merged.push({ ...range });
	});

	return merged;
};

export const highlightNameMatch = (
	name: string,
	query?: string,
	isOrganization?: boolean
): string => {
	const trimmedQuery = query?.trim();

	if (!name) return '';
	if (!trimmedQuery) return escapeHtml(name);

	const terms = trimmedQuery
		.split(/\s+/)
		.filter(Boolean)
		.flatMap(term => term.split(/-+/).filter(Boolean));
	const parts = name.split(/(\s+)/);

	const words: WordEntry[] = [];
	let sequence = 0;
	let partOffset = 0;

	parts.forEach(part => {
		if (part.trim()) {
			Array.from(part.matchAll(SUB_WORD)).forEach(subWordMatch => {
				const rawSubWord = subWordMatch[0];
				const prefixLength =
					rawSubWord.match(LEADING_PUNCTUATION)?.[0].length ?? 0;
				const core = rawSubWord.slice(prefixLength);
				if (!core) return;

				words.push({
					sequence: sequence++,
					start: partOffset + (subWordMatch.index ?? 0) + prefixLength,
					core,
					normalizedCore: normalize(core)
				});
			});
		}

		partOffset += part.length;
	});

	const matchedRanges: MatchRange[] = [];
	const consumed = new Set<number>();

	const addMatch = (word: WordEntry, length: number) => {
		matchedRanges.push({ start: word.start, length });
		consumed.add(word.sequence);
	};

	terms.forEach(term => {
		const remaining = words.filter(word => !consumed.has(word.sequence));

		const directMatch = remaining.find(word => isPrefixMatch(word.core, term));

		if (directMatch) {
			addMatch(directMatch, term.length);
			return;
		}

		const synonymMatch = isOrganization
			? remaining.find(word => hasSynonymPrefixMatch(word.core, term))
			: undefined;

		if (synonymMatch) {
			addMatch(synonymMatch, synonymMatch.core.length);
			return;
		}

		if (term.length < FUZZY_MIN_TERM_LENGTH) return;

		const fuzzyMatch = findFuzzyMatch(remaining, term);

		if (fuzzyMatch) {
			addMatch(fuzzyMatch, fuzzyMatch.core.length);
		}
	});

	matchedRanges.sort((a, b) => a.start - b.start);
	const mergedRanges = mergeAdjacentRanges(matchedRanges, name);

	let cursor = 0;
	let output = '';

	mergedRanges.forEach(range => {
		output += escapeHtml(name.slice(cursor, range.start));
		output += `<mark>${escapeHtml(
			name.slice(range.start, range.start + range.length)
		)}</mark>`;
		cursor = range.start + range.length;
	});

	output += escapeHtml(name.slice(cursor));
	return output;
};
